/**
 * DCS Preview Plugin for Vue
 *
 * Registers a supplied ribbon component as a global `DcsPreviewRibbon`
 * component.  The ribbon handles its own visibility — it only renders on
 * `preview.duffcloudservices.com` and hides everywhere else (localhost,
 * production domains, and inside the visual page editor iframe).
 *
 * Why does the caller pass the component in?  Because this file is compiled
 * by tsup (esbuild) which has no `.vue` SFC loader.  Keeping the raw `.vue`
 * import out of the compiled plugins bundle avoids the build error while
 * still letting consumer code (which *does* run through Vite) resolve the
 * SFC at dev/build time.
 *
 * @example VitePress theme/index.ts
 * ```typescript
 * import { dcsPreviewPlugin } from '@duffcloudservices/cms/plugins'
 * import PreviewRibbon from '@duffcloudservices/cms/components'
 *
 * export default {
 *   Layout,
 *   enhanceApp({ app }) {
 *     app.use(dcsPreviewPlugin(PreviewRibbon))
 *   }
 * }
 * ```
 */

import { defineComponent, h, type App, type Component, type Plugin } from 'vue'

export interface DcsPreviewPluginOptions {
  /**
   * Override the version string displayed on the ribbon.
   * If omitted, the ribbon auto-detects from VITE_SITE_VERSION or the API.
   */
  version?: string | null
}

/**
 * Creates and returns the DCS Preview plugin.
 *
 * When installed, it registers the supplied ribbon component as a global
 * `DcsPreviewRibbon` component.  Add `<DcsPreviewRibbon />` to your root
 * Layout, or use the `dcsEditorPlugin` Vite plugin which injects it via
 * `transformIndexHtml`.
 *
 * @param ribbonComponent - The PreviewRibbon SFC (imported by the consumer)
 * @param options          - Optional configuration
 */
export function dcsPreviewPlugin(
  ribbonComponent: Component,
  options: DcsPreviewPluginOptions = {},
): Plugin {
  const Wrapper = defineComponent({
    name: 'DcsPreviewRibbon',
    setup() {
      return () => h(ribbonComponent, { version: options.version ?? null })
    },
  })

  return {
    install(app: App) {
      app.component('DcsPreviewRibbon', Wrapper)

      if (options.version !== undefined) {
        app.provide('__dcs_preview_version__', options.version)
      }
    },
  }
}
